import React, { Component } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  Stack,
  SimpleGrid,
  Icon,
  VStack,
  HStack,
  Center,
  Image
} from '@chakra-ui/react';
import {
  FiMessageSquare,
  FiUsers,
  FiTrendingUp,
  FiShield,
  FiZap,
  FiCheck
} from 'react-icons/fi';

// Feature Card Component
const FeatureCard = ({ icon, title, description }) => {
  return (
    <VStack
      bg="white"
      p={8}
      borderRadius="xl"
      boxShadow="lg"
      align="start"
      spacing={4}
      transition="all 0.3s"
      _hover={{
        transform: 'translateY(-8px)',
        boxShadow: '2xl',
      }}
    >
      <Flex
        w={16}
        h={16}
        align="center"
        justify="center"
        borderRadius="lg"
        bg="blue.50"
      >
        <Icon as={icon} w={8} h={8} color="blue.600" />
      </Flex>
      <Heading size="md" color="gray.800">
        {title}
      </Heading>
      <Text color="gray.600" fontSize="md">
        {description}
      </Text>
    </VStack>
  );
};

// Benefit Item Component
const BenefitItem = ({ text, color = "blue.500" }) => {
  return (
    <HStack spacing={3} align="start">
      <Icon as={FiCheck} w={6} h={6} color={color} mt={1} />
      <Text fontSize="lg" color="gray.700">
        {text}
      </Text>
    </HStack>
  );
};

class LandingPage extends Component {
  scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  render() {
    return (
      <Box bg="neutral.50">
        {/* Hero Section */}
        <Box
          bgGradient="linear(to-br, blue.200, blue.400)"
          color="white"
          minH="100vh"
          position="relative"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          {/* Background Pattern */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            opacity={0.1}
            bgImage="radial-gradient(circle, white 1px, transparent 1px)"
            bgSize="30px 30px"
          />

          {/* Top Navigation */}
          <Container maxW="container.xl" position="relative" zIndex={2}>
            <Flex justify="flex-end" align="center" py={6}>
              <HStack spacing={4}>
                <Button
                  variant="ghost"
                  color="white"
                  fontSize="md"
                  fontWeight="medium"
                  borderRadius="full"
                  px={6}
                  _hover={{
                    bg: 'whiteAlpha.200',
                  }}
                  onClick={() => this.scrollToSection('wat-is-het')}
                >
                  Wat is het?
                </Button>
                <Button
                  as="a"
                  href="mailto:info@metdemos.app"
                  bg="whiteAlpha.300"
                  color="white"
                  fontSize="md"
                  fontWeight="medium"
                  borderRadius="full"
                  px={6}
                  backdropFilter="blur(10px)"
                  _hover={{
                    bg: 'whiteAlpha.400',
                    textDecoration: 'none',
                  }}
                >
                  Contact
                </Button>
              </HStack>
            </Flex>
          </Container>

          <Container maxW="container.xl" position="relative" flex="1" display="flex" alignItems="center" justifyContent="center" px={{ base: 4, md: 8 }}>
            <Stack spacing={8} align="center" textAlign="center" width="100%">
              <VStack spacing={4} width="100%">
                <Heading
                  as="h1"
                  fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
                  fontWeight="bold"
                  lineHeight="1.2"
                  width="100%"
                >
                  Jouw stem,
                  <br />
                  <Text as="span" color="blue.100">
                    direct gehoord.
                  </Text>
                </Heading>
                <Text
                  fontSize={{ base: 'lg', md: 'xl' }}
                  maxW="2xl"
                  color="blue.50"
                >
                  <Text as="span" color="blue.400">
                    Demos 
                  </Text> verbindt burgers en vertegenwoordigers op een moderne,
                  directe manier. Deel je ideeën voor een betere samenleving en zie echte verandering.
                </Text>
              </VStack>

              <Stack
                direction={{ base: 'column', sm: 'row' }}
                spacing={4}
                pt={4}
              >
                <Button
                  size="lg"
                  bg="blue.700"
                  color="white"
                  px={8}
                  py={6}
                  fontSize="lg"
                  fontWeight="bold"
                  borderRadius="full"
                  boxShadow="xl"
                  _hover={{
                    bg: 'blue.800',
                    transform: 'translateY(-2px)',
                    boxShadow: '2xl',
                  }}
                  onClick={() => this.scrollToSection('wat-is-het')}
                >
                  Meer Informatie
                </Button>
                <Button
                  as="a"
                  href="mailto:info@metdemos.app"
                  size="lg"
                  bg="whiteAlpha.300"
                  color="white"
                  px={8}
                  py={6}
                  fontSize="lg"
                  fontWeight="bold"
                  borderRadius="full"
                  backdropFilter="blur(10px)"
                  _hover={{
                    bg: 'whiteAlpha.400',
                    textDecoration: 'none',
                  }}
                >
                  Neem Contact Op
                </Button>
              </Stack>

              {/* Trust Badge */}
              <HStack spacing={2} pt={8} color="blue.100">
                <Icon as={FiShield} w={5} h={5} />
                <Text fontSize="sm">
                  100% veilig en transparant • AVG compliant
                </Text>
              </HStack>
            </Stack>
          </Container>
        </Box>

        {/* What is Demos Section */}
        <Container id ="wat-is-het" maxW="container.xl" py={20}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={16} alignItems="center">
            {/* Platform Screenshot - Left */}
            <Box
              borderRadius="xl"
              overflow="hidden"
              boxShadow="2xl"
              bg="white"
              border="2px solid"
              borderColor="gray.200"
            >
              <Image
                src="/platform-screenshot.png"
                alt="Demos Platform Screenshot"
                w="100%"
                h="100%"
                objectFit="cover"
                display="block"
              />
            </Box>

            {/* Explanation - Right */}
            <VStack align="start" spacing={6}>
              <Heading
                as="h2"
                fontSize={{ base: '3xl', md: '4xl' }}
                color="gray.800"
              >
                Wat is <Text as="span" color="blue.400">Demos</Text>?
              </Heading>
              <Text fontSize="lg" color="gray.600" lineHeight="tall">
                Demos is een modern digitaal platform dat de kloof tussen burgers en
                hun gekozen vertegenwoordigers overbrugt.
              </Text>
              <Text fontSize="lg" color="gray.600" lineHeight="tall">
                Als <Text as="span" fontWeight="semibold" color="blue.400">burger</Text> kun
                je eenvoudig je ideeën, zorgen en suggesties delen voor een betere samenleving, direct aan vertegenwoordigers.
              </Text>
              <Text fontSize="lg" color="gray.600" lineHeight="tall">
                Als <Text as="span" fontWeight="semibold" color="blue.500">vertegenwoordiger</Text> krijg
                je direct inzicht in wat er leeft onder je achterban, met gegenereerde
                samenvattingen van de belangrijkste thema's.
              </Text>
              <Box
                bg="blue.50"
                p={6}
                borderRadius="lg"
                borderLeft="4px solid"
                borderColor="blue.400"
              >
                <Text fontSize="md" color="gray.700" fontStyle="italic">
                  "Een directe lijn tussen jou en je volksvertegenwoordigers, zonder complexiteit."
                </Text>
              </Box>
              <Button
                size="lg"
                colorScheme="blue"
                mt={4}
                borderRadius="full"
                px={8}
                rightIcon={<Icon as={FiZap} />}
                onClick={() => this.scrollToSection('hoe-het-werkt')}
              >
                Ontdek Hoe Het Werkt
              </Button>
            </VStack>
          </SimpleGrid>
        </Container>

        {/* Features Section */}
        <Container maxW="container.xl" py={20}>
          <VStack spacing={4} mb={16} textAlign="center">
            <Heading
              as="h2"
              fontSize={{ base: '3xl', md: '4xl' }}
              color="gray.800"
            >
              Waarom <Text as="span" color="blue.400">Demos</Text>?
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="2xl">
              Modern platform voor echte democratische participatie
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            <FeatureCard
              icon={FiMessageSquare}
              title="Direct Contact"
              description="Dien eenvoudig suggesties in en communiceer rechtstreeks met je volksvertegenwoordigers via geïntegreerd berichtensysteem."
            />
            <FeatureCard
              icon={FiTrendingUp}
              title="AI Analyse"
              description="Geavanceerde AI analyseert alle suggesties en identificeert belangrijke thema's en trends voor vertegenwoordigers."
            />
            <FeatureCard
              icon={FiUsers}
              title="Transparantie"
              description="Zie de status van je suggesties en volg hoe vertegenwoordigers reageren op input van burgers."
            />
            <FeatureCard
              icon={FiZap}
              title="Gebruiksvriendelijk"
              description="Intuïtieve interface maakt het makkelijk om je stem te laten horen, zonder technische kennis."
            />
            <FeatureCard
              icon={FiShield}
              title="Privacy Eerst"
              description="Jouw gegevens zijn veilig. We volgen strikte privacyrichtlijnen en zijn volledig GDPR compliant."
            />
            <FeatureCard
              icon={FiCheck}
              title="Resultaten"
              description="Zie daadwerkelijk resultaat van je inbreng met statusupdates en implementatie tracking."
            />
          </SimpleGrid>
        </Container>

        {/* How It Works Section */}
        <Box id="hoe-het-werkt" bg="white" py={20}>
          <Container maxW="container.xl">
            <VStack spacing={4} mb={16} textAlign="center">
              <Heading
                as="h2"
                fontSize={{ base: '3xl', md: '4xl' }}
                color="gray.800"
              >
                Hoe werkt <Text as="span" color="blue.400">
                    Demos 
                  </Text>?
              </Heading>
              <Text fontSize="xl" color="gray.600" maxW="2xl">
                In drie simpele stappen naar meer invloed
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={12}>
              <VStack spacing={4}>
                <Center
                  w={20}
                  h={20}
                  borderRadius="full"
                  bg="blue.100"
                  color="blue.600"
                  fontSize="3xl"
                  fontWeight="bold"
                >
                  1
                </Center>
                <Heading size="md" color="gray.800" textAlign="center">
                  Account Aanmaken
                </Heading>
                <Text color="gray.600" textAlign="center">
                  Maak gratis een account aan als burger of vertegenwoordiger.
                  Kies jouw rol en begin direct.
                </Text>
              </VStack>

              <VStack spacing={4}>
                <Center
                  w={20}
                  h={20}
                  borderRadius="full"
                  bg="blue.100"
                  color="blue.600"
                  fontSize="3xl"
                  fontWeight="bold"
                >
                  2
                </Center>
                <Heading size="md" color="gray.800" textAlign="center">
                  Suggesties Delen
                </Heading>
                <Text color="gray.600" textAlign="center">
                  Als burger deel je eenvoudig je ideeën en suggesties. Als
                  vertegenwoordiger bekijk je alle input.
                </Text>
              </VStack>

              <VStack spacing={4}>
                <Center
                  w={20}
                  h={20}
                  borderRadius="full"
                  bg="blue.100"
                  color="blue.600"
                  fontSize="3xl"
                  fontWeight="bold"
                >
                  3
                </Center>
                <Heading size="md" color="gray.800" textAlign="center">
                  Volg Resultaten
                </Heading>
                <Text color="gray.600" textAlign="center">
                  Zie hoe je suggesties worden verwerkt en volg de voortgang van
                  ingediende voorstellen.
                </Text>
              </VStack>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Benefits Section */}
        <Box bg="blue.50" py={20}>
          <Container maxW="container.xl">
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={16} alignItems="center">
              <VStack align="start" spacing={6}>
                <Heading
                  as="h2"
                  fontSize={{ base: '3xl', md: '4xl' }}
                  color="gray.800"
                >
                  Voor <Text as="span" color="blue.400">
                    Burgers 
                  </Text>
                </Heading>
                <Text fontSize="lg" color="gray.600">
                  Maak het verschil in jouw gemeenschap
                </Text>
                <Stack spacing={4} pt={4}>
                  <BenefitItem text="Deel eenvoudig je ideeën en zorgen" color="blue.400" />
                  <BenefitItem text="Communiceer direct met vertegenwoordigers" color="blue.400" />
                  <BenefitItem text="Zie welke onderwerpen trending zijn" color="blue.400" />
                  <BenefitItem text="Volg de status van je suggesties" color="blue.400" />
                  <BenefitItem text="Krijg antwoord op je vragen" color="blue.400" />
                </Stack>
                <Button
                  as="a"
                  href="mailto:info@metdemos.app?subject=Interesse%20als%20burger"
                  size="lg"
                  bg="blue.400"
                  color="white"
                  mt={4}
                  borderRadius="full"
                  px={8}
                  _hover={{
                    bg: 'blue.500',
                    textDecoration: 'none',
                  }}
                >
                  Neem Contact Op
                </Button>
              </VStack>

              <VStack align="start" spacing={6}>
                <Heading
                  as="h2"
                  fontSize={{ base: '3xl', md: '4xl' }}
                  color="gray.800"
                >
                  Voor <Text as="span" color="blue.500">
                    Vertegenwoordigers 
                  </Text>
                </Heading>
                <Text fontSize="lg" color="gray.600">
                  Blijf verbonden met je achterban
                </Text>
                <Stack spacing={4} pt={4}>
                  <BenefitItem text="Overzicht van alle suggesties in één platform" />
                  <BenefitItem text="AI-gegenereerde samenvattingen van hoofdthema's" />
                  <BenefitItem text="Trending topics en prioriteiten in kaart" />
                  <BenefitItem text="Direct contact met burgers" />
                  <BenefitItem text="Beheer en track implementaties" />
                </Stack>
                <Button
                  as="a"
                  href="mailto:info@metdemos.app?subject=Interesse%20als%20vertegenwoordiger"
                  size="lg"
                  bg="blue.500"
                  color="white"
                  mt={4}
                  borderRadius="full"
                  px={8}
                  _hover={{
                    bg: 'blue.600',
                    textDecoration: 'none',
                  }}
                >
                  Neem Contact Op
                </Button>
              </VStack>
            </SimpleGrid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box
          bgGradient="linear(to-r, blue.200, blue.400)"
          color="white"
          py={40}
        >
          <Container maxW="container.md">
            <VStack spacing={8} textAlign="center">
              <Heading
                as="h2"
                fontSize={{ base: '3xl', md: '4xl' }}
                fontWeight="bold"
              >
                Klaar om je stem te laten horen?
              </Heading>
              <Text fontSize="xl" maxW="2xl" color="blue.50">
                Word onderdeel van een moderne democratie waar elke stem telt.
                Start vandaag nog gratis.
              </Text>
              <Stack
                direction={{ base: 'column', sm: 'row' }}
                spacing={4}
                pt={4}
              >
                <Button
                  as="a"
                  href="mailto:info@metdemos.app"
                  size="lg"
                  bg="blue.700"
                  color="white"
                  px={8}
                  py={6}
                  fontSize="lg"
                  fontWeight="bold"
                  borderRadius="full"
                  boxShadow="xl"
                  _hover={{
                    bg: 'blue.800',
                    transform: 'translateY(-2px)',
                    boxShadow: '2xl',
                    textDecoration: 'none',
                  }}
                >
                  Neem Contact Op
                </Button>
              </Stack>
            </VStack>
          </Container>
        </Box>

        {/* Footer */}
        <Box bg="blue.50" color="gray.600" py={12}>
          <Container maxW="container.xl">
            <VStack spacing={4}>
              <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align="center"
                gap={6}
                width="100%"
              >
                <Text fontSize="sm">
                  ©2026 <Text as="span" color="blue.400">Demos</Text>. Alle rechten voorbehouden.
                </Text>
                <HStack spacing={6} flexWrap="wrap" justify="center">
                  <Text
                    fontSize="sm"
                    cursor="pointer"
                    _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                    onClick={() => this.props.onRouteChange('privacyPolicy')}
                  >
                    Privacybeleid
                  </Text>
                  <Text
                    fontSize="sm"
                    cursor="pointer"
                    _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                    onClick={() => this.props.onRouteChange('termsOfService')}
                  >
                    Algemene Voorwaarden
                  </Text>
                  <Text
                    as="a"
                    href="mailto:info@metdemos.app"
                    fontSize="sm"
                    cursor="pointer"
                    _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                  >
                    Contact
                  </Text>
                </HStack>
              </Flex>
              <Text fontSize="xs" color="gray.500" textAlign="center">
                Demos is volledig AVG compliant. Uw privacy en gegevens zijn veilig bij ons.
              </Text>
            </VStack>
          </Container>
        </Box>
      </Box>
    );
  }
}

export default LandingPage;
